import {useMutation, useQueryClient} from '@github-ui/react-query'
import {fetchCAPI} from '../utils/fetch-capi'
import {taskApiPath, taskByIdApiPath} from '../utils/api-url-helpers'
import {agentTaskQueryKey} from './use-task'
import {agentTasksQueryKey} from './useTasks'
import type {HydratedAPITask} from '../types/api-task'

type TaskSharingStatus = 'unshared' | 'shared'

interface UseUpdateTaskSharingStatusArgs {
  owner?: string
  repo?: string
  taskId?: string
}

interface UpdateTaskSharingStatusVariables {
  sharing_status: TaskSharingStatus
}

export function useUpdateTaskSharingStatus({owner, repo, taskId}: UseUpdateTaskSharingStatusArgs) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['updateTaskSharingStatus', owner, repo, taskId],
    mutationFn: async ({sharing_status}: UpdateTaskSharingStatusVariables) => {
      if (!taskId) {
        throw new Error('taskId is required')
      }

      const path = owner && repo ? taskApiPath(owner, repo, taskId) : taskByIdApiPath(taskId)
      const response = await fetchCAPI({
        path,
        method: 'PATCH',
        body: {sharing_status},
      })
      return response.json()
    },
    onSuccess: (_data, variables) => {
      if (!taskId) return

      const taskQueryKey = agentTaskQueryKey(owner, repo, taskId)
      queryClient.setQueryData<HydratedAPITask>(taskQueryKey, oldData => {
        if (!oldData) return oldData
        return {...oldData, sharing_status: variables.sharing_status}
      })

      queryClient.setQueriesData<[Array<{id?: string; sharing_status?: TaskSharingStatus}>, ...unknown[]]>(
        {queryKey: ['repo-tasks', owner, repo]},
        oldData => {
          if (!oldData || !Array.isArray(oldData)) return oldData
          const [taskList, ...rest] = oldData
          if (!Array.isArray(taskList)) return oldData
          return [taskList.map(t => (t.id === taskId ? {...t, sharing_status: variables.sharing_status} : t)), ...rest]
        },
      )

      queryClient.setQueriesData<[Array<{id?: string; sharing_status?: TaskSharingStatus}>, ...unknown[]]>(
        {queryKey: agentTasksQueryKey},
        oldData => {
          if (!oldData || !Array.isArray(oldData)) return oldData
          const [taskList, ...rest] = oldData
          if (!Array.isArray(taskList)) return oldData
          return [taskList.map(t => (t.id === taskId ? {...t, sharing_status: variables.sharing_status} : t)), ...rest]
        },
      )
    },
  })
}
